import React, { useState, useEffect } from 'react';
import { 
  Zap, 
  ArrowLeft, 
  ArrowRight, 
  ShieldCheck, 
  Award, 
  Sparkles, 
  Code2, 
  Layers, 
  Phone, 
  CheckCircle2, 
  Laptop, 
  ExternalLink,
  ChevronDown,
  Cpu,
  Globe2,
  TrendingUp,
  GraduationCap,
  Briefcase,
  Languages,
  Search,
  BookOpen,
  X,
  FileCheck2,
  Building2,
  BarChart3
} from 'lucide-react';
import { Language, AppPage } from '../types';
import { translations } from '../data/translations';
import founderPortraitImg from '../assets/images/founder_arash_portrait_720w.webp';
import founderPortrait480w from '../assets/images/founder_arash_portrait_480w.webp';
import { MatrixCodeRain } from './MatrixCodeRain';
import { SorenaLogo } from './SorenaLogo';

interface ExecutiveHeroBannerProps {
  lang: Language;
  onOpenCalculator: () => void;
  onExplorePortfolio: () => void;
  onOpenContact: () => void;
  onNavigate?: (page: AppPage) => void;
}

export const ExecutiveHeroBanner: React.FC<ExecutiveHeroBannerProps> = ({
  lang,
  onOpenCalculator,
  onExplorePortfolio,
  onOpenContact,
  onNavigate
}) => {
  const [isBioModalOpen, setIsBioModalOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsBioModalOpen(false);
      }
    };
    if (isBioModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isBioModalOpen]);

  const t = translations[lang];
  const profile = t.executiveProfile;
  const ArrowIcon = lang === 'fa' ? ArrowLeft : ArrowRight;
  const activePortrait = founderPortraitImg;

  return (
    <div className="w-full relative overflow-hidden bg-[#020617] border-b border-slate-800/80">
      
      {/* 100% Width Panorama Background Container */}
      <div className="w-full relative min-h-[580px] lg:min-h-[640px] flex items-center">
        
        {/* Background Image with 100% Width Expansion & Responsive WebP */}
        <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none z-0">
          <picture className="w-full h-full block">
            <source
              media="(max-width: 640px)"
              srcSet="/images/hero_panorama/hero_panorama_bg_480w.webp"
              type="image/webp"
            />
            <source
              media="(max-width: 1024px)"
              srcSet="/images/hero_panorama/hero_panorama_bg_768w.webp"
              type="image/webp"
            />
            <img
              src="/images/hero_panorama/hero_panorama_bg.webp"
              alt={lang === 'fa' ? 'طراحی وب‌سایت و مهندسی معماری نرم‌افزار سورنا آی‌تی' : 'Sorena IT Enterprise Web Architecture Panoramic Banner'}
              referrerPolicy="no-referrer"
              width={1280}
              height={714}
              fetchPriority="high"
              loading="eager"
              decoding="async"
              className="w-full h-full object-cover object-center opacity-30 md:scale-105 md:transition-transform md:duration-1000 md:ease-out"
            />
          </picture>

          {/* Full-Hero Ambient Code Stream Effect (Desktop only to keep mobile GPU at 60fps) */}
          <div className="hidden md:block absolute inset-0 w-full h-full overflow-hidden pointer-events-none z-[1]">
            <MatrixCodeRain
              opacity={0.25}
              speed={0.16}
              colorMode="gold-amber"
              className="w-full h-full"
            />
          </div>
          
          {/* Cybernetic Gradient Overlays for High-Contrast Legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0c0704] via-[#0c0704]/85 to-[#0c0704]/90 lg:from-[#0c0704] lg:via-[#0c0704]/70 lg:to-[#0c0704]/40 z-[2]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0503] via-transparent to-[#0c0704]/80 z-[2]" />
          
          {/* Ambient Warm Gold & Amber Lighting Spheres (Desktop only to prevent mobile GPU blur raster bottlenecks) */}
          <div className="hidden md:block absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-amber-600/20 rounded-full blur-[160px] z-[2]" />
          <div className="hidden md:block absolute bottom-10 right-10 w-[450px] h-[450px] bg-amber-700/15 rounded-full blur-[160px] z-[2]" />
        </div>

        {/* Content Container spanning full responsive grid */}
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-16 lg:py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left Column: Executive Credentials & Headline (Col 1-7) */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-start">
              
              {/* Executive Status Badges */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#180e07]/90 border border-amber-500/40 text-amber-200 text-xs font-semibold shadow-xl shadow-amber-950/40 backdrop-blur-xl">
                  <span className="flex h-2 w-2 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                  </span>
                  <span className="font-bold">
                    {lang === 'fa' 
                      ? 'آرش کاویانی‌پور — برنامه‌نویس، متخصص ارشد وردپرس و مدرس' 
                      : 'Arash Kavianipoor — Programmer, WordPress Expert & Senior Instructor'}
                  </span>
                </div>

                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0c0704]/90 border border-amber-500/40 text-amber-200 text-[11px] font-medium backdrop-blur-md shadow-sm shadow-amber-500/10">
                  <SorenaLogo className="w-3.5 h-3.5" glow />
                  <span>
                    {lang === 'fa' 
                      ? 'مالک وب‌سایت sorena-it.com و sorena-it.ir' 
                      : 'Owner of sorena-it.com & sorena-it.ir'}
                  </span>
                </div>

                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#241308] border border-amber-600/40 text-amber-300 text-[11px] font-medium backdrop-blur-md">
                  <BookOpen className="w-3.5 h-3.5 text-amber-400" />
                  <span>
                    {lang === 'fa' 
                      ? 'مدرس ارشد طراحی سایت (از راه دور)' 
                      : 'Senior Remote Web Instructor'}
                  </span>
                </div>
              </div>

              {/* Main Headline */}
              <div className="space-y-2">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.25]">
                  {lang === 'fa' ? (
                    <>
                      <span className="block">معماری و توسعه تخصصی</span>
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500">
                        وبسایت‌ها و اپلیکیشن‌های مدرن
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="block">Enterprise Web Engineering &</span>
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500">
                        Custom Scalable Platforms
                      </span>
                    </>
                  )}
                </h1>

                <p className="text-stone-300 text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0 font-normal">
                  {lang === 'fa'
                    ? 'طراحی و توسعه وب‌سایت‌های مدرن، افزونه‌نویسی پیشرفته وردپرس، سامانه‌های اختصاصی React و سئو تکنیکال تحت مدیریت مهندس آرش کاویانی‌پور (فوق لیسانس نرم‌افزار) با +۱۲ سال سابقه توسعه، ۲ سال مدیریت بروکر فارکس، تسلط به زبان آلمانی و برنامه‌نویسی ربات‌های MQL5 و Pine Script.'
                    : 'Bespoke web platforms, senior WordPress plugin engineering, and high-performance React architectures led by Arash Kavianipoor (M.Sc. in Software Engineering) with 12+ years experience, 2 years Forex broker management, German fluency, and algorithmic trading expertise.'}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="pt-2 flex flex-wrap items-center justify-center lg:justify-start gap-3">
                <button
                  id="executive-order-cta"
                  onClick={() => onNavigate ? onNavigate('calculator') : onOpenCalculator()}
                  className="px-5 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 hover:from-amber-400 hover:to-yellow-400 text-stone-950 font-black text-sm sm:text-base shadow-xl shadow-amber-600/30 transition-all duration-200 flex items-center gap-2 transform hover:-translate-y-0.5 cursor-pointer"
                >
                  <Sparkles className="w-4 h-4 text-stone-950" />
                  <span>
                    {lang === 'fa' 
                      ? 'محاسبه آنلاین هزینه و زمان پروژه' 
                      : 'Calculate Project Timeline & Cost'}
                  </span>
                  <ArrowIcon className="w-4 h-4 text-stone-950" />
                </button>

                <button
                  id="executive-bio-modal-cta"
                  onClick={() => setIsBioModalOpen(true)}
                  className="px-4 py-3.5 rounded-xl bg-[#180e07]/90 hover:bg-[#241308] text-amber-200 hover:text-white font-bold text-sm sm:text-base border border-amber-600/40 hover:border-amber-400 transition-all duration-200 backdrop-blur-xl flex items-center gap-2 shadow-lg shadow-amber-950/40 cursor-pointer"
                >
                  <FileCheck2 className="w-4 h-4 text-amber-400" />
                  <span>
                    {lang === 'fa' 
                      ? 'مشاهده رزومه و سوابق رسمی مدیریت' 
                      : 'View Official Bio & Career'}
                  </span>
                </button>

                <button
                  id="executive-campaign-cta"
                  onClick={() => onNavigate ? onNavigate('campaign') : (window.location.hash = '#campaign')}
                  className="px-4 py-3.5 rounded-xl bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-black text-sm sm:text-base transition-all duration-200 shadow-lg shadow-amber-600/30 hover:scale-[1.02] flex items-center gap-2 cursor-pointer"
                >
                  <Sparkles className="w-4 h-4 text-white" />
                  <span>
                    {lang === 'fa' 
                      ? 'کمپین تخفیف ۱۰۰ به ۳۰ میلیون' 
                      : '$1,000 to $300 Campaign'}
                  </span>
                  <span className="px-1.5 py-0.5 rounded-md bg-white/20 text-white text-[10px] font-black">
                    {lang === 'fa' ? '۷۰٪' : '70%'}
                  </span>
                </button>

                <button
                  id="executive-algo-cta"
                  onClick={() => onNavigate ? onNavigate('algo-trading') : (window.location.hash = '#algo-trading')}
                  className="px-4 py-3.5 rounded-xl bg-[#1e1008] hover:bg-[#2a170b] text-amber-200 hover:text-white font-bold text-sm sm:text-base border border-amber-600/40 hover:border-amber-400 transition-all duration-200 backdrop-blur-xl flex items-center gap-2 shadow-lg shadow-amber-950/40 cursor-pointer"
                >
                  <TrendingUp className="w-4 h-4 text-amber-400 animate-pulse" />
                  <span>
                    {lang === 'fa' 
                      ? 'کدنویسی فارکس (MQ5 / Pine)' 
                      : 'Forex Algo (MQ5 / Pine)'}
                  </span>
                </button>

                <button
                  id="executive-contact-cta"
                  onClick={() => onNavigate ? onNavigate('contact') : onOpenContact()}
                  className="px-4 py-3.5 rounded-xl bg-[#140b06] hover:bg-[#241308] text-stone-300 hover:text-white font-medium text-xs sm:text-sm border border-amber-900/40 hover:border-amber-600 transition-all backdrop-blur-md flex items-center gap-1.5 cursor-pointer"
                >
                  <Phone className="w-3.5 h-3.5 text-amber-400" />
                  <span>
                    {lang === 'fa' 
                      ? 'مشاوره و تماس' 
                      : 'Contact'}
                  </span>
                </button>
              </div>

              {/* Key Credentials Strip */}
              <div className="pt-2 grid grid-cols-2 sm:grid-cols-4 gap-2.5 max-w-2xl mx-auto lg:mx-0">
                <div className="p-2.5 rounded-xl bg-[#180e07]/80 backdrop-blur-xl border border-amber-900/40 text-start">
                  <div className="text-[11px] text-stone-400 flex items-center gap-1 mb-0.5">
                    <Award className="w-3.5 h-3.5 text-amber-400" />
                    <span>{lang === 'fa' ? 'سابقه توسعه وب' : 'Experience'}</span>
                  </div>
                  <div className="text-sm sm:text-base font-extrabold text-white">
                    {lang === 'fa' ? '+۱۲ سال (از ۲۰۱۴)' : '12+ Years'}
                  </div>
                </div>

                <div className="p-2.5 rounded-xl bg-[#180e07]/80 backdrop-blur-xl border border-amber-900/40 text-start">
                  <div className="text-[11px] text-stone-400 flex items-center gap-1 mb-0.5">
                    <Briefcase className="w-3.5 h-3.5 text-amber-400" />
                    <span>{lang === 'fa' ? 'مدیریت بروکر فارکس' : 'Forex Broker'}</span>
                  </div>
                  <div className="text-sm sm:text-base font-extrabold text-amber-400">
                    {lang === 'fa' ? '۲ سال مدیریت' : '2 Years Mgmt'}
                  </div>
                </div>

                <div className="p-2.5 rounded-xl bg-[#180e07]/80 backdrop-blur-xl border border-amber-900/40 text-start">
                  <div className="text-[11px] text-stone-400 flex items-center gap-1 mb-0.5">
                    <Search className="w-3.5 h-3.5 text-amber-400" />
                    <span>{lang === 'fa' ? 'سئو و رتبه گوگل' : 'SEO Specialist'}</span>
                  </div>
                  <div className="text-sm sm:text-base font-extrabold text-white">
                    {lang === 'fa' ? '۴ سال متمرکز' : '4+ Yrs SEO'}
                  </div>
                </div>

                <div className="p-2.5 rounded-xl bg-[#180e07]/80 backdrop-blur-xl border border-amber-900/40 text-start">
                  <div className="text-[11px] text-stone-400 flex items-center gap-1 mb-0.5">
                    <GraduationCap className="w-3.5 h-3.5 text-amber-400" />
                    <span>{lang === 'fa' ? 'تحصیلات آکادمیک' : 'Academic Degree'}</span>
                  </div>
                  <div className="text-sm sm:text-base font-extrabold text-white">
                    {lang === 'fa' ? 'فوق لیسانس نرم‌افزار' : 'M.Sc. Software'}
                  </div>
                </div>
              </div>

            </div>

            {/* Right Column: High-End Expanded Executive Showcase (Col 8-12) */}
            <div className="lg:col-span-5 relative flex flex-col items-center p-2 sm:p-4 group">
              
              {/* Executive Portrait Frame with 3D Depth & Rim Lighting */}
              <div className="relative z-10 w-full max-w-sm sm:max-w-md rounded-3xl p-1 bg-gradient-to-b from-amber-500/50 via-amber-800/60 to-yellow-600/40 shadow-2xl shadow-amber-950/80 md:backdrop-blur-2xl transition-all duration-300 hover:shadow-amber-500/25">
                
                {/* Glow ring behind portrait - desktop only to prevent mobile GPU blur bottleneck */}
                <div className="hidden md:block absolute -inset-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-3xl blur-xl opacity-30 -z-10 group-hover:opacity-50 transition-opacity duration-500" />

                <div className="relative rounded-[22px] overflow-hidden bg-[#0c0704] border border-amber-900/50 md:backdrop-blur-md">
                  
                  {/* Portrait Image Container with High Dynamic Range & Robust Box Model */}
                  <div className="relative z-10 aspect-[3/4] w-full overflow-hidden bg-stone-950">
                    <picture className="relative w-full h-full block" itemScope itemType="https://schema.org/ImageObject">
                      <meta itemProp="name" content={lang === 'fa' ? 'آرش کاویانی پور طراح وبسایت' : 'Arash Kavianipoor web designer'} />
                      <meta itemProp="caption" content={lang === 'fa' ? 'آرش کاویانی پور طراح وبسایت | مهندس معمار وب و برنامه‌نویس ارشد وردپرس و نکست جی‌اس' : 'Arash Kavianipoor web designer | Senior Web Architect & Full-Stack Developer'} />
                      <meta itemProp="author" content="Arash Kavianipoor" />
                      <meta itemProp="contentUrl" content={activePortrait} />
                      <source
                        media="(max-width: 640px)"
                        srcSet={founderPortrait480w}
                        type="image/webp"
                      />
                      <source
                        media="(min-width: 641px)"
                        srcSet={activePortrait}
                        type="image/webp"
                      />
                      <img
                        id="founder-portrait-img"
                        src={founderPortrait480w}
                        width={480}
                        height={640}
                        fetchPriority="high"
                        loading="eager"
                        decoding="async"
                        alt={lang === 'fa' 
                          ? 'آرش کاویانی پور طراح وبسایت | Arash Kavianipoor web designer'
                          : 'Arash Kavianipoor web designer | آرش کاویانی پور طراح وبسایت'}
                        title={lang === 'fa' 
                          ? 'آرش کاویانی پور طراح وبسایت | Arash Kavianipoor web designer'
                          : 'Arash Kavianipoor web designer | آرش کاویانی پور طراح وبسایت'}
                        itemProp="image"
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-cover object-top md:filter md:contrast-[1.05] md:brightness-[1.02] md:transition-transform md:duration-500 md:hover:scale-105"
                      />
                    </picture>

                    {/* Subtle bottom fade to blend with card info */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0c0704] via-transparent to-transparent pointer-events-none" />
                    
                    {/* Corner Tag: Lead Architect - Compact on mobile to avoid badge collision */}
                    <div className="absolute top-2.5 start-2.5 sm:top-4 sm:start-4 flex items-center gap-1 sm:gap-1.5 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full bg-[#0c0704]/95 md:backdrop-blur-md border border-amber-500/40 text-amber-200 text-[10px] sm:text-[11px] font-bold shadow-lg max-w-[62%]">
                      <Code2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-400 shrink-0" />
                      <span className="truncate">
                        {lang === 'fa' 
                          ? 'متخصص ارشد وردپرس و برنامه‌نویس' 
                          : 'WordPress & Senior Specialist'}
                      </span>
                    </div>

                    {/* Official Sorena IT Brand Seal Stamp - Scaled neatly for mobile */}
                    <div className="absolute top-2.5 end-2.5 sm:top-4 sm:end-4 flex items-center gap-1 sm:gap-1.5 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-[#0c0704]/95 md:backdrop-blur-md border border-amber-500/40 text-amber-200 text-[9px] sm:text-[10px] font-mono font-bold shadow-lg shadow-amber-950/50">
                      <SorenaLogo className="w-3 h-3 sm:w-3.5 sm:h-3.5" glow={false} />
                      <span>SORENA IT</span>
                    </div>
                  </div>

                  {/* Integrated Details Panel under Portrait */}
                  <div className="relative z-10 p-3.5 sm:p-5 bg-[#180e07] md:backdrop-blur-xl border-t border-amber-900/50 space-y-2.5 sm:space-y-3">
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-base sm:text-lg font-black text-white">
                          {lang === 'fa' 
                            ? 'مهندس آرش کاویانی‌پور' 
                            : 'Arash Kavianipoor'}
                        </div>
                        <div className="text-xs text-amber-400 font-medium">
                          {lang === 'fa' 
                            ? 'مالک وب‌سایت‌های sorena-it.com و sorena-it.ir' 
                            : 'Owner & Founder of sorena-it.com & sorena-it.ir'}
                        </div>
                        <div className="text-[11px] text-amber-300 font-medium mt-0.5">
                          {lang === 'fa' 
                            ? 'مدرس ارشد طراحی سایت (از راه دور) | ۲ سال مدیریت بروکر' 
                            : 'Senior Remote Web Instructor | 2 Yrs Broker Mgmt'}
                        </div>
                      </div>
                      
                      <button
                        onClick={() => setIsBioModalOpen(true)}
                        className="p-2 rounded-xl bg-amber-500/10 hover:bg-amber-500/25 border border-amber-500/40 text-amber-300 transition-colors cursor-pointer"
                        title={lang === 'fa' ? 'مشاهده رزومه کامل' : 'View Full Bio'}
                      >
                        <FileCheck2 className="w-5 h-5 text-amber-400" />
                      </button>
                    </div>

                    {/* Specialized Stacks Chips */}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      <span className="px-2 py-0.5 rounded-md bg-[#241308] border border-amber-500/40 text-[10px] font-mono text-amber-200 font-bold">
                        ⚡ WordPress & Senior Plugins
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-[#2f190c] border border-amber-600/40 text-[10px] font-mono text-amber-200 font-bold">
                        🌲 MQL5 & Pine Script v5
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-stone-950 border border-amber-900/40 text-[10px] font-mono text-amber-300">
                        React 19 & Next.js
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-stone-950 border border-amber-900/40 text-[10px] font-mono text-amber-300">
                        Google Certified 2024
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-stone-950 border border-amber-900/40 text-[10px] font-mono text-amber-300">
                        {lang === 'fa' ? 'فوق لیسانس نرم‌افزار' : 'M.Sc. Software Eng'}
                      </span>
                    </div>

                  </div>

                </div>
              </div>

            </div>

          </div>
        </div>
      </div>

      {/* Comprehensive Official Profile & Bio Modal */}
      {isBioModalOpen && (
        <div 
          id="bio-modal-backdrop"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-stone-950/85 backdrop-blur-xl overflow-y-auto cursor-pointer"
          onClick={() => setIsBioModalOpen(false)}
        >
          <div 
            id="bio-modal-container"
            className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl bg-[#120a05] border border-amber-500/40 p-6 sm:p-8 shadow-2xl shadow-amber-950/80 text-start space-y-6 cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            
            {/* Modal Header */}
            <div className="flex items-start justify-between border-b border-amber-900/40 pb-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#0c0704] p-2 border border-amber-500/40 flex items-center justify-center shadow-lg shadow-amber-500/20 shrink-0">
                  <SorenaLogo className="w-full h-full" glow />
                </div>
                <div className="space-y-1">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#241308] border border-amber-500/40 text-amber-300 text-xs font-mono">
                    <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                    <span>{profile.sectionBadge}</span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black text-white">
                    {profile.title}
                  </h2>
                  <p className="text-xs sm:text-sm text-amber-300 font-mono">
                    {profile.roleTitle}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsBioModalOpen(false)}
                className="p-2 rounded-xl bg-[#241308] hover:bg-[#341b0b] text-stone-400 hover:text-white border border-amber-900/50 transition-colors cursor-pointer"
                title={lang === 'fa' ? 'بستن پنجره' : 'Close'}
              >
                <X className="w-5 h-5 text-amber-300" />
              </button>
            </div>

            {/* Current Executive Roles Badges */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {profile.currentPositions.map((pos, idx) => (
                <div key={idx} className="p-3 rounded-xl bg-[#180e07]/90 border border-amber-900/40 flex items-center gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0" />
                  <span className="text-xs sm:text-sm font-semibold text-stone-200">{pos}</span>
                </div>
              ))}
            </div>

            {/* Professional Summary Statement */}
            <div className="p-5 rounded-2xl bg-[#180e07]/90 border border-amber-500/30 space-y-3">
              <div className="flex items-center gap-2 text-amber-300 text-sm font-bold">
                <FileCheck2 className="w-4 h-4 text-amber-400" />
                <span>
                  {lang === 'fa' 
                    ? 'خلاصه حرفه‌ای و بیوگرافی رسمی (Professional Summary)' 
                    : 'Official Professional Summary'}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-stone-300 text-justify">
                {profile.bioSummary}
              </p>
            </div>

            {/* Core Pillars / Experience Highlights Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              {/* Education & Academic Background */}
              <div className="p-4 rounded-2xl bg-[#180e07]/80 border border-amber-900/40 space-y-2">
                <div className="flex items-center gap-2 text-amber-400 text-xs font-bold">
                  <GraduationCap className="w-4 h-4 text-amber-400" />
                  <span>
                    {lang === 'fa' 
                      ? 'تحصیلات آکادمیک' 
                      : 'Academic Education'}
                  </span>
                </div>
                <div className="text-sm font-bold text-white">
                  {lang === 'fa' 
                    ? 'فوق لیسانس مهندسی نرم‌افزار (M.Sc. Software Engineering)' 
                    : 'Master of Science (M.Sc.) in Software Engineering'}
                </div>
                <div className="text-xs text-stone-400">
                  {lang === 'fa' 
                    ? 'کارشناسی ارشد مهندسی نرم‌افزار — تخصص در معماری پلتفرم‌های مقیاس‌پذیر و هوش مصنوعی' 
                    : 'Graduate Degree in Software Engineering — Specialized in Scalable Web Platforms & AI'}
                </div>
              </div>

              {/* Forex Broker Management */}
              <div className="p-4 rounded-2xl bg-[#180e07]/80 border border-amber-900/40 space-y-2">
                <div className="flex items-center gap-2 text-amber-400 text-xs font-bold">
                  <Briefcase className="w-4 h-4 text-amber-400" />
                  <span>
                    {lang === 'fa' 
                      ? 'سابقه مدیریت بروکر فارکس' 
                      : 'Forex Broker Management'}
                  </span>
                </div>
                <div className="text-sm font-bold text-white">
                  {lang === 'fa' 
                    ? '۲ سال سابقه مدیریت بروکر بین‌المللی فارکس' 
                    : '2 Years Forex Broker Operations Management'}
                </div>
                <div className="text-xs text-stone-400">
                  {lang === 'fa' 
                    ? 'مدیریت عملیات فنی، زیرساخت سرورها و مدیریت ریسک در Walfiner Broker' 
                    : 'Executive operations & technical infrastructure at Walfiner Broker'}
                </div>
              </div>

              {/* Languages & International Teaching */}
              <div className="p-4 rounded-2xl bg-[#180e07]/80 border border-amber-900/40 space-y-2">
                <div className="flex items-center gap-2 text-amber-300 text-xs font-bold">
                  <Languages className="w-4 h-4 text-amber-300" />
                  <span>
                    {lang === 'fa' 
                      ? 'زبان‌های بین‌المللی و تدریس' 
                      : 'Languages & Remote Teaching'}
                  </span>
                </div>
                <div className="text-sm font-bold text-white">
                  {lang === 'fa' 
                    ? 'تسلط روان به زبان آلمانی + مدرس ارشد از راه دور' 
                    : 'Fluent German (C1) + Senior Remote Instructor'}
                </div>
                <div className="text-xs text-stone-400">
                  {lang === 'fa' 
                    ? 'سابقه تدریس وب و تعاملات فنی بین‌المللی با کارفرمایان اروپایی' 
                    : 'International web collaboration and active remote programming mentorship'}
                </div>
              </div>

              {/* SEO Specialist & Google Certificate */}
              <div className="p-4 rounded-2xl bg-[#180e07]/80 border border-amber-900/40 space-y-2">
                <div className="flex items-center gap-2 text-amber-400 text-xs font-bold">
                  <Search className="w-4 h-4 text-amber-400" />
                  <span>
                    {lang === 'fa' 
                      ? 'تخصص سئو و مدرک رسمی گوگل' 
                      : 'SEO Specialist & Google Cert'}
                  </span>
                </div>
                <div className="text-sm font-bold text-white">
                  {lang === 'fa' 
                    ? 'متخصص سئو (۴ سال متمرکز) + گواهینامه گوگل ۲۰۲۴' 
                    : 'SEO Specialist (4 Yrs) + Google Certified 2024'}
                </div>
                <div className="text-xs text-stone-400">
                  Google Digital Marketing & E-commerce Professional Certificate (2024)
                </div>
              </div>

            </div>

            {/* Career Timeline */}
            <div className="space-y-3">
              <div className="text-sm font-bold text-stone-200 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-amber-400" />
                <span>
                  {lang === 'fa' 
                    ? 'سوابق مدیریتی و سازمانی (شرکت‌های ام‌گاز، والفاینر بروکر و آکادمی ژرف‌گستر)' 
                    : 'Corporate Track Record (M-Gas, Walfiner Broker, Jarfgostar)'}
                </span>
              </div>

              <div className="space-y-2.5">
                {profile.careerHighlights.map((hl, idx) => (
                  <div key={idx} className="p-3.5 rounded-xl bg-[#180e07]/60 border border-amber-900/40 flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                    <div className="space-y-1">
                      <div className="text-xs font-bold text-amber-300">{hl.title}</div>
                      <div className="text-xs text-stone-400 leading-relaxed">{hl.desc}</div>
                    </div>
                    <span className="shrink-0 text-[10px] font-mono text-amber-400 bg-[#241308] border border-amber-800/40 px-2.5 py-1 rounded-full self-start">
                      {hl.year}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Key Expertise List */}
            <div className="space-y-2">
              <div className="text-xs font-bold text-stone-300">
                {lang === 'fa' 
                  ? 'مهارت‌ها و حوزه‌های کلیدی تخصص (Key Expertise):' 
                  : 'Key Technical Expertise:'}
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.keySkills.map((sk, idx) => (
                  <span key={idx} className="px-2.5 py-1 rounded-lg bg-[#180e07] border border-amber-900/40 text-xs text-stone-300 font-mono">
                    ✓ {sk}
                  </span>
                ))}
              </div>
            </div>

            {/* Modal Footer Actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-amber-900/40">
              <div className="text-xs text-stone-400 flex items-center gap-2">
                <Globe2 className="w-4 h-4 text-amber-400" />
                <span>sorena-it.com | sorena-it.ir</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setIsBioModalOpen(false);
                    onOpenContact();
                  }}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-stone-950 font-bold text-xs transition-colors"
                >
                  {lang === 'fa' 
                    ? 'درخواست مشاوره مستقیم با مهندس کاویانی‌پور' 
                    : 'Consult with Arash Kavianipoor'}
                </button>
                <button
                  onClick={() => setIsBioModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-[#180e07] hover:bg-[#241308] text-stone-300 text-xs border border-amber-900/40 transition-colors"
                >
                  {lang === 'fa' ? 'بستن' : 'Close'}
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};
